import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { CheckCircle, ChatCircleText, WhatsappLogo, House, Copy } from "@phosphor-icons/react";
import { useSettings } from "@/lib/settings";
import { trackConversion } from "@/lib/track";
import MetaPixel, { firePixelEvent } from "@/components/MetaPixel";

export default function CanadaIndiaThanks() {
  const settings = useSettings();
  const location = useLocation();
  const [search] = useSearchParams();
  const stateRef = location.state?.reference;
  const stateStatus = location.state?.status;
  const reference = stateRef || search.get("ref") || "";
  const status = stateStatus || search.get("status") || "";
  const qualified = status === "HOT" || status === "WARM";
  const whatsapp = (settings.whatsapp_number || "+18253658805").replace(/\D/g, "");
  const pixelId = settings?.meta_pixel_id || "";
  const leadFiredRef = useRef(false);

  // Fire the Meta `Lead` standard event ONCE per successful submission.
  // Guards:
  //   1. ref must be truthy (direct visits without submission have no reference → no Lead)
  //   2. in-component ref prevents double-fire from React 18 strict-mode re-mount
  //   3. sessionStorage key keyed by reference prevents double-fire on page refresh
  //   4. settings.meta_pixel_id must be loaded so the Pixel can actually be
  //      injected synchronously (fixes a race where settings load async and
  //      window.fbq wasn't yet defined when this effect first ran).
  useEffect(() => {
    if (!reference) return;
    if (!pixelId) return; // wait for settings load
    if (leadFiredRef.current) return;
    const sessionKey = `oba_lead_fired_${reference}`;
    try {
      if (window.sessionStorage?.getItem(sessionKey)) return;
    } catch (_) { /* storage unavailable — proceed */ }
    leadFiredRef.current = true;
    try { window.sessionStorage?.setItem(sessionKey, "1"); } catch (_) { /* noop */ }
    // Fire the Meta standard Lead event — ensurePixelInjected() runs inside
    // firePixelEvent, so this works even if MetaPixel's own useEffect hasn't
    // run yet on this render pass.
    firePixelEvent(pixelId, "Lead", { value: 0, currency: "USD" });
    // Also fire the campaign thank-you event for Google/GA4 context (does
    // NOT trigger Meta events — fbEventMap only maps lead/contact/etc).
    trackConversion("lp_canada_india_thanks", { value: 1, payload: { reference, status } });
  }, [reference, status, pixelId]);

  function copyRef() {
    if (!reference) return;
    try { navigator.clipboard?.writeText(reference); } catch (_) { /* noop */ }
  }

  return (
    <div className="fade-in bg-gos-bone min-h-screen flex items-center py-20" data-testid="lp-canada-india-thanks">
      {/* Meta Pixel — SCOPED to this landing page family only */}
      <MetaPixel />      <div className="container-gos max-w-2xl mx-auto">
        <div className="bg-white border border-gos-border rounded-sm p-8 md:p-10 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-gos-maple/15 flex items-center justify-center">
            <CheckCircle size={40} weight="fill" className="text-gos-maple" />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-gos-charcoal mt-6 tracking-tight">
            {qualified ? "You're a strong fit." : "Thanks for reaching out."}
          </h1>
          <p className="text-gos-slate mt-4 leading-relaxed max-w-lg mx-auto">
            {qualified
              ? "Based on your answers, you look like a great candidate for a Canadian setup. Pick a time to speak with our team or message us on WhatsApp — whichever is easier."
              : "We've received your details and one of our advisors will reach out shortly on WhatsApp or email with the next steps."}
          </p>

          {reference && (
            <div className="mt-8 bg-gos-bone border border-gos-border rounded-sm p-5 inline-block max-w-md">
              <div className="overline !text-gos-muted mb-2">Your reference number</div>
              <div className="flex items-center justify-center gap-3">
                <div className="font-mono text-xl text-gos-navy tracking-wider" data-testid="lp-thanks-ref">
                  {reference}
                </div>
                <button
                  type="button"
                  onClick={copyRef}
                  data-testid="lp-thanks-ref-copy"
                  className="inline-flex items-center gap-1 text-xs text-gos-slate hover:text-gos-navy border border-gos-border rounded-sm px-2 py-1 transition"
                  aria-label="Copy reference"
                  title="Copy reference"
                >
                  <Copy size={13} weight="bold" /> Copy
                </button>
              </div>
              <p className="text-xs text-gos-muted mt-3 leading-relaxed">
                Please save this reference. Quote it in any email or WhatsApp message so our team can locate your request instantly.
              </p>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact?country=canada&source=lp-india" className="btn-primary" data-testid="lp-thanks-book">
              <ChatCircleText size={16} weight="bold" /> Book Consultation
            </Link>
            <a
              href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi OBA, I just submitted the India → Canada form${reference ? ` (ref ${reference})` : ""}.`)}`}
              target="_blank" rel="noreferrer"
              onClick={() => trackConversion("whatsapp_click", { value: 10 })}
              className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-5 py-2.5 rounded-sm text-sm hover:brightness-110 transition"
              data-testid="lp-thanks-whatsapp"
            >
              <WhatsappLogo size={18} weight="fill" /> WhatsApp Us
            </a>
            <Link to="/" className="btn-secondary" data-testid="lp-thanks-home">
              <House size={14} weight="bold" /> Back to home
            </Link>
          </div>

          <p className="text-xs text-gos-muted italic mt-8 leading-relaxed max-w-md mx-auto">
            Please note: we do not guarantee bank-account approval, immigration or visa outcomes,
            tax results or any government or third-party approvals. These remain subject to the
            applicable authority or provider.
          </p>
        </div>
      </div>
    </div>
  );
}
